import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { StrapiConfig } from "../../utils/Setting";
import { updateStepMappingToCompleted } from "../../utils/stepMappingUtils";

export const usePortfolioAPI = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Get Strapi API token
  const getStrapiToken = () => {
    if (!StrapiConfig.apiToken) {
      throw new Error("REACT_STRAPI_API_TOKEN not configured");
    }
    return StrapiConfig.apiToken;
  };

  // Strapi-specific upload function
  const uploadToStrapi = async (file, strapiToken) => {
    const formData = new FormData();
    formData.append("files", file);

    const response = await fetch(`${StrapiConfig.url}/api/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${strapiToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Upload failed: ${response.status} ${response.statusText}`,
      );
    }

    const result = await response.json();
    return result[0]; // Strapi returns array of uploaded files
  };

  // Upload images
  const uploadImages = useCallback(async (files) => {
    if (!files || files.length === 0) return [];

    const fileArray = Array.isArray(files) ? files : Array.from(files);

    // Validate file types
    const validFiles = fileArray.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return [];

    setUploading(true);
    try {
      const strapiToken = getStrapiToken();

      const uploadPromises = validFiles.map(async (file) => {
        const uploadedFile = await uploadToStrapi(file, strapiToken);
        return uploadedFile;
      });

      const uploadedImages = await Promise.all(uploadPromises);
      toast.success(`${validFiles.length} image(s) uploaded successfully!`);
      return uploadedImages;
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(
        `Failed to upload images: ${error.message || "Unknown error"}`,
      );
      return [];
    } finally {
      setUploading(false);
    }
  }, []);

  // Get or create contractor
  const getOrCreateContractor = async (userData, strapiToken) => {
    // Use organization ID (contractor_data.id) to find/create contractor
    const organizationId = userData?.contractor_data?.id || userData?.id;

    if (!organizationId) {
      throw new Error(
        "Organization ID (contractor_data.id) not found in userData",
      );
    }

    const contractorResponse = await fetch(
      `${StrapiConfig.url}/api/contractors?filters[external_id][$eq]=${organizationId}`,
      {
        headers: { Authorization: `Bearer ${strapiToken}` },
      },
    );

    if (!contractorResponse.ok) {
      const errorText = await contractorResponse.text();
      throw new Error(
        `Failed to fetch contractor: ${contractorResponse.status}`,
      );
    }

    const contractorData = await contractorResponse.json();
    let contractor = contractorData.data?.[0];

    if (!contractor) {
      const newContractorResponse = await fetch(
        `${StrapiConfig.url}/api/contractors`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${strapiToken}`,
          },
          body: JSON.stringify({
            data: {
              external_id: organizationId.toString(),
              company_name:
                userData?.name ||
                userData?.contractor_data?.name ||
                "My Company",
            },
          }),
        },
      );

      if (!newContractorResponse.ok) {
        const errorText = await newContractorResponse.text();
        throw new Error(
          `Failed to create contractor: ${newContractorResponse.status} - ${errorText}`,
        );
      }

      const newContractorData = await newContractorResponse.json();
      contractor = newContractorData.data;
    }

    return contractor;
  };

  // Create or update portfolio (portfolio now contains project data directly)
  const saveProject = useCallback(
    async (
      formData,
      imageOrder,
      portfolio,
      editingProject,
      userData,
      token,
    ) => {
      setLoading(true);
      try {
        const strapiToken = getStrapiToken();

        // Get or create contractor
        const contractor = await getOrCreateContractor(userData, strapiToken);

        // Build gallery array with image and order fields
        const galleryData = formData.images.map((img) => ({
          image: img.id,
          order: imageOrder[img.id] || 0,
        }));

        // Portfolio now contains all project fields directly
        const portfolioData = {
          data: {
            project_name: formData.title,
            project_description: formData.description,
            location: formData.location,
            unit_type: formData.unitType,
            date: formData.projectDate
              ? formData.projectDate.toISOString()
              : null,
            gallery: galleryData,
            contractor: contractor.id,
          },
        };

        const isEditing = editingProject && editingProject.id;
        const url = isEditing
          ? `${StrapiConfig.url}/api/portfolios/${editingProject.documentId || editingProject.id}`
          : `${StrapiConfig.url}/api/portfolios`;
        const method = isEditing ? "PUT" : "POST";

        const response = await fetch(url, {
          method: method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${strapiToken}`,
          },
          body: JSON.stringify(portfolioData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to ${isEditing ? "update" : "create"} project`,
          );
        }

        const responseData = await response.json();
        toast.success(
          `Project ${isEditing ? "updated" : "created"} successfully!`,
        );

        // Update PORTFOLIO step mapping to COMPLETED after successful project save
        await updateStepMappingToCompleted(userData.id, "PORTFOLIO", token);

        return responseData;
      } catch (error) {
        console.error(
          `Project ${editingProject ? "update" : "creation"} error:`,
          error,
        );
        toast.error(
          `Failed to ${editingProject ? "update" : "create"} project: ${error.message}`,
        );
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Fetch a single portfolio (project) with all images for editing
  const fetchProjectForEditing = useCallback(async (projectId) => {
    try {
      const strapiToken = getStrapiToken();

      const response = await fetch(
        `${StrapiConfig.url}/api/portfolios/${projectId}?populate[gallery][populate]=image`,
        {
          headers: { Authorization: `Bearer ${strapiToken}` },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch project for editing");
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error("Error fetching project for editing:", error);
      throw error;
    }
  }, []);

  return {
    loading,
    uploading,
    uploadImages,
    saveProject,
    fetchProjectForEditing,
  };
};
